import { faCircleInfo } from '@fortawesome/pro-duotone-svg-icons';
import { faChevronDown, faChevronLeft, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, CircularProgress, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Typography, useMediaQuery } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { sxCircularProgressCenter, sxFormError, sxLabel, sxMenuItem, sxSubTitle } from '../../../../../assets/styles/CommonSxProps';
import LightTooltip from '../../../../../components/LightTooltip';
import ModalFeedBack from '../../../../../components/ModalFeedBack';
import { Cidade } from '../../../../../interfaces/entities/Cidade';
import { PassoDadosPrestador } from '../../../../../interfaces/entities/Reembolso/PassoDadosPrestador';
import { StepDadosPrestadorProps } from '../../../../../interfaces/props/Financeiro/Reembolso/StepDadosPrestadorProps';
import { DadosPrestadorValidation } from '../../../../../interfaces/validation/Reembolso/DadosPrestadorValidation';
import { getCidades, getInformacoesPrestador, prestadorCredenciado } from '../../../../../services/Financeiro/ReembolsoService';
import { sxButton } from '../sxProps';
import './styles.css';
import { sxInputSmall } from './sxProps';
import { cnpjMask } from '../../../../../utils/CnpjMask';
import { cpfMask } from '../../../../../utils/CpfMask';
import { isValidCNPJ, isValidCPF } from '../../../../../utils/CpfCnpjValidator';
import { cpfCnpjMask } from '../../../../../utils/CpfCnpjMask';
import FilterSelect from '../../../../../components/FilterSelect';
import { getStateFullName } from '../../../../../utils/StateMap';

const StepDadosPrestador: React.FC<StepDadosPrestadorProps> = ({ handleBack, handleProceed, setEspecialidades, userRequestedToEditInfos, dadosPrestador }) => {

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingVerificar, setLoadingVerificar] = useState<boolean>(false);
  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const [passoDadosPrestador, setPassoDadosPrestador] = useState<PassoDadosPrestador>();
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  const formik = useFormik({
    initialValues: {
      tipoDocumento: userRequestedToEditInfos && dadosPrestador?.tipoDocumento ? dadosPrestador.tipoDocumento : '',
      numeroDocumento: userRequestedToEditInfos && dadosPrestador?.numeroDocumento ? dadosPrestador.numeroDocumento : '',
      nomePrestador: userRequestedToEditInfos && dadosPrestador?.nomePrestador ? dadosPrestador.nomePrestador : '',
      uf: userRequestedToEditInfos && dadosPrestador?.uf ? dadosPrestador.uf : '',
      cidade: userRequestedToEditInfos && dadosPrestador?.cidade ? dadosPrestador.cidade : ''
    },
    onSubmit: () => {
      verificarPrestador()
    },
    validationSchema: DadosPrestadorValidation,
    validateOnChange: false
  });

  const fetchInfosPrestador = async () => {
    setLoading(true);
    const response = await getInformacoesPrestador();
    if (response?.status === 200) {
      setPassoDadosPrestador(response.data);
    } else {
      console.error(response);
      openModalError('Erro', 'Ocorreu um erro. Tente novamente em instantes.');
    }
    setLoading(false);
  }

  const fetchCidades = async (idUf: string) => {
    const response = await getCidades(idUf);
    if (response?.status === 200) {
      setCidades(response.data);
    } else {
      console.error(response);
      openModalError('Erro', 'Ocorreu um erro. Tente novamente em instantes.');
    }
  }

  const handleSelectUf = (idUf: string, cidade: string) => {
    formik.setFieldValue('cidade', cidade)
    formik.setFieldValue('uf', idUf);
    fetchCidades(idUf);
  }

  useEffect(() => {
    fetchInfosPrestador();
    validateIfIsEditing();
  }, []);

  const validateIfIsEditing = () => {
    if (dadosPrestador?.uf) {
      handleSelectUf(dadosPrestador.uf, dadosPrestador.cidade);
    }
  }

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const verificarPrestador = () => {
    setLoadingVerificar(true);
    const numberDocument = cpfCnpjMask(formik.values.numeroDocumento);

    if (formik.values.tipoDocumento === 'PJ' && !isValidCNPJ(formik.values.numeroDocumento)) {
      formik.setFieldError('numeroDocumento', 'Insira um CNPJ válido e correto');
      setLoadingVerificar(false);
      return;
    }

    if (formik.values.tipoDocumento === 'PF' && !isValidCPF(formik.values.numeroDocumento)) {
      formik.setFieldError('numeroDocumento', 'Insira um CPF válido e correto');
      setLoadingVerificar(false);
      return;
    }

    prestadorCredenciado(numberDocument)
      .then((response) => {
        if (response === "S") {
          formik.setFieldError('numeroDocumento', 'Informe outro prestador');

          setModalFeedback({
            icon: 'error',
            title: "Reembolso não permitido",
            subTitle: 'De acordo com as regras de seu contrato, consultas e exames realizados com profissionais ou estabelecimentos da rede de atendimento do sistema nacional Unimed são inelegíveis para reembolso.',
            textButton: 'Fechar',
            showButtonModal: true,
            closeAction: () => setModalFeedback(null),
            buttonAction: () => setModalFeedback(null)
          });
          return;
        }

        handleProceed(formik.values);
        setEspecialidades(passoDadosPrestador!.lstEspecialidade);
      })
      .catch(e => {
        setModalFeedback({
          icon: 'error',
          title: "Erro",
          subTitle: e || "Ocorreu um erro na aplicação. Tente novamente mais tarde.",
          textButton: 'Fechar',
          showButtonModal: true,
          closeAction: () => setModalFeedback(null),
          buttonAction: () => setModalFeedback(null)
        });
      })
      .finally(() => setLoadingVerificar(false));
  };


  return (
    <>
      <form className='feature-container' onSubmit={formik.handleSubmit}>
        {loading ? <CircularProgress sx={sxCircularProgressCenter} /> :
          <>
            <Typography sx={sxSubTitle} component="h2">Dados do prestador</Typography>
            <div className="half-divided-input">
              <div className='input-holder'>
                <div className='input-label-holder'>
                  <Typography sx={sxLabel} tabIndex={0}>
                    Tipo de documento &nbsp;
                    </Typography>
                </div>
                <Select
                  sx={sxInputSmall}
                  value={formik.values.tipoDocumento}
                  fullWidth={mobileScreen}
                  onChange={(e) => {
                    formik.setFieldValue('tipoDocumento', e.target.value)
                    formik.setFieldValue('numeroDocumento', '')
                  }}
                  displayEmpty
                  renderValue={
                    formik.values.tipoDocumento !== "" ? undefined : () => <span className='placeholder'>Selecione o tipo de documento</span>
                  }
                  IconComponent={(props) => <FontAwesomeIcon icon={faChevronDown} {...props} />}
                >
                  {!!passoDadosPrestador?.lstTipoDocumento.length &&
                    passoDadosPrestador.lstTipoDocumento.map((tipoDocumento) => (
                      tipoDocumento.descr && <MenuItem key={tipoDocumento.id} sx={sxMenuItem} value={tipoDocumento.id}>{tipoDocumento.descr}</MenuItem>
                    ))
                  }
                </Select>
                <div className='tooltip-holder left-tipo-doc'>
                <LightTooltip
                      title={'Preencha os campos a seguir com os dados do profissional ou empresa que realizou o atendimento/ item (conforme consta no documento fiscal - recibo ou nota).'}
                    >
                      <FontAwesomeIcon icon={faCircleInfo as IconDefinition} className='fa-info-icon-18x18'/>
                    </LightTooltip>
                </div>
                <Typography sx={sxFormError}>{formik.errors.tipoDocumento}</Typography>
              </div>
              <div className='input-holder'>
                <div className='input-label-holder'>
                <Typography sx={sxLabel} tabIndex={0}>
                  Número do documento &nbsp;
                </Typography>
                </div>
                <label aria-label="Número do documento" htmlFor='num-doc-input' />
                <OutlinedInput
                  type='text'
                  inputMode='numeric'
                  id='num-doc-input'
                  value={formik.values.numeroDocumento}
                  fullWidth={mobileScreen}
                  placeholder='Exemplos: 999.999.999-99 (cpf) ou AB.CDE.FGH/IJKL-99 (cnpj)'
                  aria-describedby={formik.errors.numeroDocumento ? "num-doc-error" : undefined}
                  onChange={(e) => {
                    let maskedValue = ''
                    if (formik.values.tipoDocumento === 'PJ') {
                      maskedValue = cnpjMask(e.target.value);
                    } else {
                      maskedValue = cpfMask(e.target.value);
                    }
                    if (maskedValue === '') {
                      formik.setFieldError('numeroDocumento', '');
                    }
                    formik.setFieldValue('numeroDocumento', maskedValue);
                  }}
                  name='numeroDocumento'
                  error={!!formik.errors.numeroDocumento}
                  maxRows={1}
                  sx={sxInputSmall}
                  disabled={!formik.values.tipoDocumento}
                   endAdornment={loadingVerificar ?
                    (
                      <InputAdornment position="end">
                        <CircularProgress size={20} sx={{ zIndex: '1' }} />
                      </InputAdornment>
                    ) : null
                  }
                />
                <div className='tooltip-holder left-num-doc'>
                <LightTooltip
                    title={'Coloque o número do CPF, se pessoa física (profissional de saúde), ou número do CNPJ, se pessoa jurídica (empresa).'}
                  >
                    <FontAwesomeIcon icon={faCircleInfo as IconDefinition} className='fa-info-icon-18x18' />
                  </LightTooltip>
                </div>
                <Typography sx={sxFormError} id="num-doc-error">{formik.errors.numeroDocumento}</Typography>
              </div>
            </div>
            <div className='input-holder'>
              <Typography sx={sxLabel} tabIndex={0}>Nome do prestador</Typography>
              <label htmlFor="nome-input" aria-label="nome do prestador" />
              <OutlinedInput
                placeholder='José da Silva'
                id="nome-input"
                type={'text'}
                fullWidth={mobileScreen}
                value={formik.values.nomePrestador}
                onChange={(e) => { formik.setFieldValue('nomePrestador', e.target.value); }}
                name='nomePrestador'
                error={!!formik.errors.nomePrestador}
                inputProps={{ maxLength: 60 }}
                maxRows={1}
                sx={sxInputSmall}
              />
              <Typography sx={sxFormError}>{formik.errors.nomePrestador}</Typography>
            </div>
            <div className="half-divided-input">
              <div className='select-input-holder'>
                <FilterSelect title='UF' disabled={false}
                  options={
                    !!passoDadosPrestador?.lstUf.length ?
                    passoDadosPrestador.lstUf.map((uf) => (
                      uf.sigla
                      ? { id: uf.id, descricao: uf.sigla, ariaDescricao: getStateFullName(uf.sigla) || '' }
                      : { id: '', descricao: '' }
                    ))
                    : []
                  }
                  handleChange={uf => handleSelectUf(uf, '')} 
                  erase={false} 
                  placeholder="Selecione o estado"
                />
                <Typography sx={sxFormError}>{formik.errors.uf}</Typography>
              </div>
              <div className='select-input-holder'>
                <FilterSelect title='Cidade' disabled={false}
                  options={!
                    !cidades?.length ?
                    cidades.map((cidade) => (
                      cidade.descricao
                      ? { id: cidade.id, descricao: cidade.descricao }
                      : { id: '', descricao: '' }
                    )) : []
                  }
                  handleChange={cidade => formik.setFieldValue('cidade', cidade)} erase={false} placeholder="Selecione o estado"
                />
                <Typography sx={sxFormError}>{formik.errors.cidade}</Typography>
              </div>
            </div>
            <Button
              variant="contained"
              size="large"
              type='submit'
              fullWidth={mobileScreen}
              sx={sxButton}
              disabled={
                !(formik.values.nomePrestador && formik.values.numeroDocumento && formik.values.cidade
                  && formik.values.tipoDocumento && formik.values.uf
                )}
            >
              <Typography>CONTINUAR</Typography>
            </Button>
            <button className="back-button" onClick={() => handleBack()} aria-label="Voltar para conta para crédito">
              <FontAwesomeIcon icon={faChevronLeft} />&nbsp;&nbsp;<u>Voltar</u>
            </button>
          </>
        }
      </form>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
        classNameModal='modal-custom'
      />

    </>
  )
}

export default StepDadosPrestador;